import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, auth, saveBooking, updateBooking, deleteBooking, createHold, releaseHold, backfillAvailability, quickBookSlot, savePrivateInfo } from './firebase';
import { CONFIGS, calculatePriceForSlot, PENDING_VERIFICATION_MINUTES } from './config';
import { getUnit, formatDatePK, formatDateTimePK } from './utils';
import { generateSlipPdf } from './pdf/generateSlipPdf';
import { getNextWebSerial } from './webSerial';
import './AdminPanel.css';

function AdminPanel({ blockedDates, questions, holds, onAddBlockedDate, onAddQuestion, onAnswerQuestion }) {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [searchDate, setSearchDate] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [answerDrafts, setAnswerDrafts] = useState({});
  const [activeTab, setActiveTab] = useState('verify');
  const [bookingFilter, setBookingFilter] = useState('all');
  const [bookingSearch, setBookingSearch] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [nicError, setNicError] = useState('');
  const [privateData, setPrivateData] = useState({});
  const [lastSavedBooking, setLastSavedBooking] = useState(null);
  const [generatingSerial, setGeneratingSerial] = useState(false);
  const [advanceDrafts, setAdvanceDrafts] = useState({});
  const [now, setNow] = useState(Date.now());

  // Ticks once a second so the "time left" countdowns on pending
  // verifications stay live without needing their own per-card timers.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Real Firebase session — firestore.rules requires this for bookingPrivate
  // reads and all admin writes, so this is what actually gates access, not
  // just which screen gets rendered.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Phone/NIC live in a separate `bookingPrivate` collection (see firebase.js)
  // so they're never fetched by the public booking page. Only load them here,
  // and only once the admin is signed in.
  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(collection(db, 'bookingPrivate'), (snapshot) => {
      const map = {};
      snapshot.docs.forEach(d => { map[d.id] = d.data(); });
      setPrivateData(map);
    });
    return () => unsubscribe();
  }, [user]);

  // Full `bookings` documents (name, totalPrice, etc.) are admin-only to read
  // now — the public site reads a minimal `availability` shadow instead (see
  // firestore.rules). This panel is the one place that still needs the real
  // thing, so it owns this subscription itself rather than receiving it as a
  // prop from a component the public page also renders.
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q,
      (snapshot) => setBookings(snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }))),
      (err) => {
        console.error('bookings listener error:', err);
        alert('⚠️ Could not load bookings from Firebase.\n\nError: ' + err.message);
      }
    );
    return () => unsubscribe();
  }, [user]);

  const withPrivateData = (b) => ({ ...b, ...(privateData[b.firestoreId] || {}) });

  const EMPTY_SCAN = {
    serialNo: '', name: '', phone: '', nicNo: '', guestCount: '',
    bookingType: 'family', checkInDate: '', day: '', slotType: '10hour-day',
    unit: 'Gold', totalPrice: '', advance: '', balance: '', paymentStatus: 'Pending',
    acCount: ''
  };
  const [scanFormData, setScanFormData] = useState(EMPTY_SCAN);

  const getDayName = (dateStr) => {
    if (!dateStr) return '';
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    return days[new Date(dateStr).getDay()];
  };

  const runScan = async (imageData) => {
    setIsScanning(true);
    try {
      const Tesseract = await import('tesseract.js');
      const { data: { text } } = await Tesseract.default.recognize(imageData, 'eng');

      const filled = {};

      // Serial number
      const serialMatch = text.match(/S\.?\s*No\.?\s*[:-]?\s*(\d{1,6})/i) || text.match(/^(\d{3,6})\b/m);
      if (serialMatch) filled.serialNo = serialMatch[1].trim();

      // Date: DD/MM/YY or DD-MM-YYYY etc.
      const dateMatch = text.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
      if (dateMatch) {
        let [, d, m, y] = dateMatch;
        if (y.length === 2) y = '20' + y;
        const iso = `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
        if (!isNaN(new Date(iso).getTime())) {
          filled.checkInDate = iso;
          filled.day = getDayName(iso);
        }
      }

      // Total amount
      const totalMatch = text.match(/Total\s*[:-]?\s*(?:Rs\.?)?\s*([\d,]+)/i);
      if (totalMatch) {
        const total = parseInt(totalMatch[1].replace(/,/g, ''));
        if (total >= 5000 && total <= 500000) {
          filled.totalPrice = String(total);
        }
      }

      // Advance — only fills in when the slip actually states it; never guessed,
      // since the real amount received isn't necessarily half of the total.
      const advMatch = text.match(/Advance\s*[:-]?\s*(?:Rs\.?)?\s*([\d,]+)/i);
      if (advMatch) {
        const adv = parseInt(advMatch[1].replace(/,/g, ''));
        if (adv >= 1000) {
          filled.advance = String(adv);
          const total = parseInt(filled.totalPrice || scanFormData.totalPrice) || 0;
          if (total > 0) filled.balance = String(Math.max(0, total - adv));
        }
      }

      // Balance
      const balMatch = text.match(/Balance\s*[:-]?\s*(?:Rs\.?)?\s*([\d,]+)/i);
      if (balMatch) {
        const bal = parseInt(balMatch[1].replace(/,/g, ''));
        if (bal >= 0) filled.balance = String(bal);
      }

      // No of persons
      const guestMatch = text.match(/(?:No\.?\s*of\s*Person|Persons?|Guests?)\s*[:-]?\s*(\d{1,3})/i);
      if (guestMatch) filled.guestCount = guestMatch[1];

      // Slot type — paper slips say "12 Hour"/"24 Hour", which are NOT the
      // same durations as today's Day/Night (10hr) or Full (22hr) slots.
      // Kept as distinct legacy values rather than guessed onto the wrong
      // current slotType — see the pending decision on how (or whether) to
      // reconcile these with live pricing/availability.
      if (/24\s*Hour/i.test(text)) filled.slotType = '24hour-legacy';
      else if (/12\s*Hour/i.test(text)) filled.slotType = '12hour-legacy';

      if (Object.keys(filled).length > 0) {
        setScanFormData(prev => ({ ...prev, ...filled }));
        alert('Scan done!\n\nFilled: ' + Object.keys(filled).join(', ') + '\n\nPlease check these and fill the rest by hand.');
      } else {
        alert('Could not read fields clearly.\nThe photo is shown above — fill the form manually.');
      }
    } catch (err) {
      console.error('Scan error:', err);
      alert('Scan failed. Please fill manually.');
    }
    setIsScanning(false);
  };

  const calculateStandardPrice = (unit, slotType, checkInDate) => {
    const cfg = CONFIGS[(unit || 'gold').toLowerCase()];
    return calculatePriceForSlot(cfg, checkInDate, slotType);
  };

  const handleLogin = async () => {
    setAuthError('');
    setSigningIn(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim(), loginPassword);
      setLoginPassword('');
    } catch (err) {
      setAuthError('❌ Sign-in failed: ' + err.message);
    }
    setSigningIn(false);
  };

  const handleLogout = () => signOut(auth);

  const pendingVerification = bookings.filter(b => b.status === 'pending_verification');
  const pendingVerificationCount = pendingVerification.length;
  // Explicit `=== false` (not just falsy) so bookings from before this
  // feature existed — which never got an infoComplete field at all — aren't
  // retroactively flagged as needing attention.
  const incompleteCount = bookings.filter(b => b.infoComplete === false).length;

  const handleVerifyPayment = async (booking) => {
    const entered = parseInt(advanceDrafts[booking.firestoreId]) || 0;
    if (entered <= 0) { alert('Enter the advance amount actually received first.'); return; }
    const balance = Math.max(0, Number(booking.totalPrice) - entered);
    try {
      await updateBooking(booking.firestoreId, {
        status: 'confirmed',
        paymentStatus: entered >= Number(booking.totalPrice) ? 'Completed' : 'Pending',
        advance: entered,
        balance,
        verifiedAt: new Date().toISOString(),
      });
      setAdvanceDrafts(prev => { const next = { ...prev }; delete next[booking.firestoreId]; return next; });
    } catch (err) {
      alert('❌ Failed to confirm booking: ' + err.message);
    }
  };

  const handleExpireNow = async (booking) => {
    if (!window.confirm(`Mark ${booking.name}'s booking as expired and release the slot?`)) return;
    try {
      await updateBooking(booking.firestoreId, { status: 'expired' });
    } catch (err) {
      alert('❌ Failed to expire booking: ' + err.message);
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const handleStartEdit = (b) => {
    setEditingId(b.firestoreId);
    setEditDraft({
      name: b.name || '',
      checkInDate: b.checkInDate || '',
      unit: getUnit(b) === 'silver' ? 'Silver' : 'Gold',
      slotType: b.slotType || '10hour-day',
      guestCount: b.guestCount || '',
      totalPrice: String(b.totalPrice || ''),
      advance: String(b.advance || ''),
      // Only relevant if still missing — see the locked/input split in the
      // edit form JSX. Pre-filling with the existing value would be
      // pointless since an already-set field can never be resaved anyway.
      phone: '',
      nicNo: '',
      serialNo: '',
    });
  };

  const handleCancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const handleSaveEdit = async (b) => {
    const totalPrice = parseInt(editDraft.totalPrice) || 0;
    const advance = parseInt(editDraft.advance) || 0;
    if (!editDraft.name.trim() || !editDraft.checkInDate) { alert('Name and date are required.'); return; }
    const nicClean = (editDraft.nicNo || '').replace(/\D/g, '');
    if (nicClean && nicClean.length !== 13) { alert('NIC must be exactly 13 digits (or leave it blank).'); return; }
    try {
      // Phone/NIC/serial can only ever be written once (bookingPrivate has no
      // update rule) — do this first so a failure here never leaves the
      // booking marked infoComplete without its paperwork actually saved.
      const newPrivate = {};
      if (!b.phone && editDraft.phone.trim())    newPrivate.phone = editDraft.phone.trim();
      if (!b.nicNo && nicClean)                  newPrivate.nicNo = nicClean;
      if (!b.serialNo && editDraft.serialNo.trim()) newPrivate.serialNo = editDraft.serialNo.trim();
      if (Object.keys(newPrivate).length > 0) {
        await savePrivateInfo(b.firestoreId, newPrivate);
      }
      await updateBooking(b.firestoreId, {
        name: editDraft.name.trim(),
        checkInDate: editDraft.checkInDate,
        unit: editDraft.unit,
        farmhouse: editDraft.unit.toLowerCase(),
        slotType: editDraft.slotType,
        guestCount: editDraft.guestCount,
        totalPrice,
        advance,
        // Balance is always derived, never a field the admin fills in directly.
        balance: Math.max(0, totalPrice - advance),
        // Saving via Edit is what "completes" a quick-booked slot — harmless
        // to re-set true on a booking that was already complete.
        infoComplete: true,
      });
      handleCancelEdit();
    } catch (err) {
      alert('❌ Failed to save changes: ' + err.message);
    }
  };

  // Soft-delete: the record stays in Firestore (status: 'cancelled') for
  // accountability, just filtered out of the slot-availability check.
  const handleCancelBooking = async (b) => {
    if (!window.confirm(`Cancel ${b.name}'s booking? The slot will be released. The record stays for your records — this is NOT a permanent delete.`)) return;
    try {
      await updateBooking(b.firestoreId, { status: 'cancelled', cancelledAt: new Date().toISOString() });
    } catch (err) {
      alert('❌ Failed to cancel booking: ' + err.message);
    }
  };

  // True hard-delete — for obvious test/duplicate entries only. Requires
  // typing the guest's name to confirm, kept visually distinct from Cancel.
  const handleHardDelete = async (b) => {
    const typed = window.prompt(`This PERMANENTLY deletes ${b.name}'s booking — it cannot be undone and will not appear anywhere, even for accountability.\n\nType the guest's name exactly ("${b.name}") to confirm:`);
    if (typed !== b.name) {
      if (typed !== null) alert('Name did not match — nothing was deleted.');
      return;
    }
    try {
      await deleteBooking(b.firestoreId);
    } catch (err) {
      alert('❌ Failed to delete booking: ' + err.message);
    }
  };

  const [syncingAvailability, setSyncingAvailability] = useState(false);

  // Fixes bookings created before the `availability` shadow collection
  // existed — those never got a companion doc, so they silently show as
  // "open" on the public site and Quick-Check even though they're real
  // bookings. Safe to run more than once.
  const handleSyncAvailability = async () => {
    if (!window.confirm(`Re-sync availability records for all ${bookings.length} bookings currently loaded? This fixes bookings made before the availability system existed.`)) return;
    setSyncingAvailability(true);
    try {
      const count = await backfillAvailability(bookings);
      alert(`✅ Synced ${count} booking(s). The public site and Quick-Check should now reflect all of them correctly.`);
    } catch (err) {
      alert('❌ Sync failed: ' + err.message);
    }
    setSyncingAvailability(false);
  };

  const HOLD_STAFF = ['Faizan', 'Shayan', 'Siraj'];
  const HOLD_DURATIONS = [
    { val: '30min', label: '30 Minutes' },
    { val: '2hr', label: '2 Hours' },
    { val: '2days', label: '2 Days' },
    { val: '7days', label: '7 Days' },
    { val: 'indefinite', label: 'Indefinite' },
  ];
  const [holdDate, setHoldDate] = useState('');
  const [holdSlotType, setHoldSlotType] = useState('10hour-day');
  const [holdStaff, setHoldStaff] = useState('');
  const [holdDuration, setHoldDuration] = useState('2hr');

  const handlePlaceHold = async (unit) => {
    if (!holdDate) { alert('Pick a date first.'); return; }
    if (!holdStaff) { alert('You must pick who is placing this hold (Faizan / Shayan / Siraj) — no anonymous holds.'); return; }
    try {
      await createHold({ unit, date: holdDate, slotType: holdSlotType, heldBy: holdStaff, duration: holdDuration });
      setHoldDate(''); setHoldStaff('');
      alert('✅ Hold placed');
    } catch (err) {
      alert('❌ Failed to place hold: ' + err.message);
    }
  };

  const handleReleaseHold = async (hold) => {
    if (!window.confirm(`Release ${hold.heldBy}'s hold on ${hold.date}?`)) return;
    try {
      await releaseHold(hold.firestoreId);
    } catch (err) {
      alert('❌ Failed to release hold: ' + err.message);
    }
  };

  const [quickBookDate, setQuickBookDate] = useState('');
  const [quickBookSlotType, setQuickBookSlotType] = useState('10hour-day');
  const [quickBooking, setQuickBooking] = useState(false);

  // Instantly occupies a slot with no guest info at all — for clearing a
  // backlog of physical slips before go-live. Unlike a hold this is a real
  // booking (never auto-expires), so it warrants a confirm step even though
  // the whole point is to be fast.
  const handleQuickBook = async (unit) => {
    if (!quickBookDate) { alert('Pick a date first.'); return; }
    if (isDateBlocked(quickBookDate, unit)) {
      alert(`❌ ${unit === 'gold' ? 'Gold' : 'Silver'} — ${quickBookDate} is blocked (maintenance/event). Quick Book isn't allowed on a blocked date.`);
      return;
    }
    const { dayBooked, nightBooked } = getSlotStatus(quickBookDate, unit);
    const conflict =
      (quickBookSlotType === '10hour-day'   && dayBooked) ||
      (quickBookSlotType === '10hour-night' && nightBooked) ||
      (quickBookSlotType === '22hour'       && (dayBooked || nightBooked));
    if (conflict) {
      alert(`❌ ${unit === 'gold' ? 'Gold' : 'Silver'} already has a booking on ${quickBookDate} for this slot. Pick a different date/slot — Quick Book can't double-book. If the existing booking is wrong, fix or cancel it first from the Bookings tab.`);
      return;
    }
    if (!window.confirm(`Mark ${unit === 'gold' ? 'Gold' : 'Silver'} — ${quickBookDate} — as BOOKED right now with no customer info yet?\n\nYou'll fill in the guest's name/phone/price later from the Bookings tab → "⚡ Needs Info".`)) return;
    setQuickBooking(true);
    try {
      await quickBookSlot({ unit, date: quickBookDate, slotType: quickBookSlotType });
      setQuickBookDate('');
      alert('✅ Slot marked as booked. Find it in the Bookings tab → "⚡ Needs Info" whenever you\'re ready to fill in the guest\'s details.');
    } catch (err) {
      alert('❌ Failed to quick-book: ' + err.message);
    }
    setQuickBooking(false);
  };

  const formatHoldAge = (createdAt) => {
    const ms = Date.now() - new Date(createdAt).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  // Oldest first, per spec, so a long-forgotten indefinite hold surfaces at
  // the top rather than getting buried under recent ones.
  const activeHolds = [...(holds || [])]
    .filter(h => h.status === 'active')
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // Booking_GOLD_W0007 / Booking_SILVER_0238 — unit + serial up front so staff
  // can find the right file at a glance in a phone/computer's downloads
  // folder instead of a pile of same-looking "slip.pdf" files.
  const slipBaseName = (booking) => {
    const unit = getUnit(booking) === 'silver' ? 'SILVER' : 'GOLD';
    const id = booking.serialNo || booking.name || 'blank';
    return `Booking_${unit}_${id}`;
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  };

  // Saves the PDF straight to the device's Downloads folder — no share sheet
  // involved, so it works the same on every browser/OS and is always available
  // even where navigator.share isn't (desktop Chrome/Firefox, some Androids).
  const handleDownloadSlip = async (booking) => {
    try {
      const blob = await generateSlipPdf(booking);
      downloadBlob(blob, `${slipBaseName(booking)}.pdf`);
    } catch (err) {
      alert('❌ Failed to generate slip: ' + err.message);
    }
  };

  // Draws the slip directly onto a canvas (see generateSlipImage.js) instead
  // of rasterizing the generated PDF — the earlier pdfjs-dist approach needed
  // a ~1.2MB worker file fetched on demand, which was slow/unreliable on
  // weak mobile connections and caused this to hang then fail outright.
  const handleDownloadSlipImage = async (booking) => {
    try {
      const { generateSlipImage } = await import('./pdf/generateSlipImage');
      const pngBlob = await generateSlipImage(booking);
      downloadBlob(pngBlob, `${slipBaseName(booking)}.png`);
    } catch (err) {
      alert('❌ Failed to generate slip image: ' + err.message);
    }
  };

  // CANCELLED-stamped versions of the same four slip actions below — a
  // printable/shareable record for a booking that fell through, distinct
  // from the 🚫 Cancel button (which changes the booking's actual status in
  // Firestore). These only ever generate a document; they never touch the
  // booking record itself.
  const handleDownloadCancelledSlip = async (booking) => {
    try {
      const blob = await generateSlipPdf(booking, { cancelled: true });
      downloadBlob(blob, `${slipBaseName(booking)}-CANCELLED.pdf`);
    } catch (err) {
      alert('❌ Failed to generate cancelled slip: ' + err.message);
    }
  };

  const handleDownloadCancelledSlipImage = async (booking) => {
    try {
      const { generateSlipImage } = await import('./pdf/generateSlipImage');
      const pngBlob = await generateSlipImage(booking, { cancelled: true });
      downloadBlob(pngBlob, `${slipBaseName(booking)}-CANCELLED.png`);
    } catch (err) {
      alert('❌ Failed to generate cancelled slip image: ' + err.message);
    }
  };

  const shareBlob = async (blob, fileName, mimeType) => {
    const file = new File([blob], fileName, { type: mimeType });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: 'Riaz Farmhouse Booking Slip' });
    } else {
      downloadBlob(blob, fileName);
    }
  };

  const handleShareSlipPdf = async (booking) => {
    try {
      const blob = await generateSlipPdf(booking);
      await shareBlob(blob, `${slipBaseName(booking)}.pdf`, 'application/pdf');
    } catch (err) {
      alert('❌ Failed to generate/share slip: ' + err.message);
    }
  };

  const handleShareSlipImage = async (booking) => {
    try {
      const { generateSlipImage } = await import('./pdf/generateSlipImage');
      const pngBlob = await generateSlipImage(booking);
      await shareBlob(pngBlob, `${slipBaseName(booking)}.png`, 'image/png');
    } catch (err) {
      alert('❌ Failed to generate/share slip image: ' + err.message);
    }
  };

  const handleShareCancelledSlipPdf = async (booking) => {
    try {
      const blob = await generateSlipPdf(booking, { cancelled: true });
      await shareBlob(blob, `${slipBaseName(booking)}-CANCELLED.pdf`, 'application/pdf');
    } catch (err) {
      alert('❌ Failed to generate/share cancelled slip: ' + err.message);
    }
  };

  const handleShareCancelledSlipImage = async (booking) => {
    try {
      const { generateSlipImage } = await import('./pdf/generateSlipImage');
      const pngBlob = await generateSlipImage(booking, { cancelled: true });
      await shareBlob(pngBlob, `${slipBaseName(booking)}-CANCELLED.png`, 'image/png');
    } catch (err) {
      alert('❌ Failed to generate/share cancelled slip image: ' + err.message);
    }
  };

  // FULLY-PAID-stamped versions — same idea as the CANCELLED ones above, but
  // for handing the guest a paid-in-full receipt/gate pass at entry. Forces
  // the balance to show as Rs. 0 and adds the green stamp; deliberately
  // drops the "Gate Pass" pill (see SlipDocument.js/generateSlipImage.js —
  // a gate pass makes sense for an upcoming visit, not a closed-out receipt).
  const handleDownloadPaidSlip = async (booking) => {
    try {
      const blob = await generateSlipPdf(booking, { paidStamp: true });
      downloadBlob(blob, `${slipBaseName(booking)}-PAID.pdf`);
    } catch (err) {
      alert('❌ Failed to generate fully-paid slip: ' + err.message);
    }
  };

  const handleDownloadPaidSlipImage = async (booking) => {
    try {
      const { generateSlipImage } = await import('./pdf/generateSlipImage');
      const pngBlob = await generateSlipImage(booking, { paidStamp: true });
      downloadBlob(pngBlob, `${slipBaseName(booking)}-PAID.png`);
    } catch (err) {
      alert('❌ Failed to generate fully-paid slip image: ' + err.message);
    }
  };

  const handleSharePaidSlipPdf = async (booking) => {
    try {
      const blob = await generateSlipPdf(booking, { paidStamp: true });
      await shareBlob(blob, `${slipBaseName(booking)}-PAID.pdf`, 'application/pdf');
    } catch (err) {
      alert('❌ Failed to generate/share fully-paid slip: ' + err.message);
    }
  };

  const handleSharePaidSlipImage = async (booking) => {
    try {
      const { generateSlipImage } = await import('./pdf/generateSlipImage');
      const pngBlob = await generateSlipImage(booking, { paidStamp: true });
      await shareBlob(pngBlob, `${slipBaseName(booking)}-PAID.png`, 'image/png');
    } catch (err) {
      alert('❌ Failed to generate/share fully-paid slip image: ' + err.message);
    }
  };

  // Pure field mapping, no validation — used both by Save Booking (after its
  // own required-field check below) and by Generate Slip, which deliberately
  // allows an all-blank form through so staff can print a blank physical-
  // style template, same as the old paper booklet.
  const mapScanFormToBooking = () => {
    const nicClean = scanFormData.nicNo.replace(/\D/g, '');
    return {
      id: Date.now(),
      serialNo: scanFormData.serialNo,
      name: scanFormData.name,
      phone: scanFormData.phone,
      nicNo: nicClean,
      guestCount: scanFormData.guestCount || '1',
      bookingType: scanFormData.bookingType,
      checkInDate: scanFormData.checkInDate,
      day: scanFormData.day,
      slotType: scanFormData.slotType,
      acCount: scanFormData.acCount,
      farmhouse: (scanFormData.unit || 'Gold').toLowerCase(),
      unit: scanFormData.unit || 'Gold',
      totalPrice: parseInt(String(scanFormData.totalPrice).replace(/[^0-9]/g, '')) || 0,
      advance: parseInt(String(scanFormData.advance).replace(/[^0-9]/g, '')) || 0,
      balance: parseInt(String(scanFormData.balance).replace(/[^0-9]/g, '')) || 0,
      paymentStatus: scanFormData.paymentStatus,
      createdAt: new Date().toISOString(),
      status: 'confirmed'
    };
  };

  // Shared by all three slip actions (PDF / Image / Share) — only NIC format
  // is checked (if one was typed at all), since generating a slip preview
  // doesn't need name/phone/date the way an actual Firestore save does.
  const draftBookingOrAlert = () => {
    const nicClean = scanFormData.nicNo.replace(/\D/g, '');
    if (nicClean && nicClean.length !== 13) {
      alert('NIC must be exactly 13 digits');
      return null;
    }
    return mapScanFormToBooking();
  };

  // Save Booking still requires these — it's a real Firestore write. Generate
  // Slip has no such requirement (see mapScanFormToBooking above).
  const buildBookingFromScanForm = () => {
    if (!scanFormData.name || !scanFormData.phone || !scanFormData.checkInDate) {
      alert('Required: Name, Contact No, and Date');
      return null;
    }
    const nicClean = scanFormData.nicNo.replace(/\D/g, '');
    if (nicClean && nicClean.length !== 13) {
      alert('NIC must be exactly 13 digits');
      return null;
    }
    return mapScanFormToBooking();
  };

  const [blockUnit, setBlockUnit] = useState('both');
  const [searchUnit, setSearchUnit] = useState('gold');

  const handleBlockDate = async () => {
    if (!searchDate || !blockReason) {
      alert('Enter date and reason');
      return;
    }
    try {
      await onAddBlockedDate({ date: searchDate, unit: blockUnit, reason: blockReason, blockedAt: new Date().toISOString() });
      setSearchDate('');
      setBlockReason('');
      alert('✅ Date blocked');
    } catch (err) {
      alert('❌ Failed to block date: ' + err.message);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion) { alert('Enter a question'); return; }
    try {
      await onAddQuestion(newQuestion);
      setNewQuestion('');
    } catch (err) {
      alert('❌ Failed to save question: ' + err.message);
    }
  };

  const handleAnswerQuestion = async (q) => {
    const answer = answerDrafts[q.firestoreId];
    if (!answer) return;
    try {
      await onAnswerQuestion(q.firestoreId, answer);
      setAnswerDrafts(prev => ({ ...prev, [q.firestoreId]: '' }));
    } catch (err) {
      alert('❌ Failed to save answer: ' + err.message);
    }
  };

  // Was blind to `holds` entirely (only ever checked real bookings) — this
  // is what Quick Book's conflict check and the Availability tab's search
  // both rely on, so an active hold was previously invisible to the admin's
  // own tools even though it correctly blocks the public site / Quick-Check.
  // Two collections feeding one availability answer through two different
  // code paths is exactly the kind of drift that causes phantom double
  // bookings, so this now folds holds into the same check.
  const getSlotStatus = (date, unit) => {
    const dateBookings = bookings.filter(b => b.checkInDate === date && getUnit(b) === unit && b.status !== 'expired' && b.status !== 'cancelled');
    let dayBooked = false, nightBooked = false;
    for (const b of dateBookings) {
      if (b.slotType === '22hour') { dayBooked = true; nightBooked = true; }
      if (b.slotType === '10hour-day' || b.slotType === '10hour') dayBooked = true;
      if (b.slotType === '10hour-night') nightBooked = true;
    }
    const activeHoldsHere = (holds || []).filter(h => h.date === date && h.unit === unit && h.status === 'active');
    for (const h of activeHoldsHere) {
      if (h.slotType === '22hour') { dayBooked = true; nightBooked = true; }
      if (h.slotType === '10hour-day') dayBooked = true;
      if (h.slotType === '10hour-night') nightBooked = true;
    }
    return { dayBooked, nightBooked, fullyBooked: dayBooked && nightBooked };
  };

  const isDateBlocked = (date, unit) => blockedDates.some(b => b.date === date && (b.unit === 'both' || b.unit === unit));

  const getNextAvailableDates = (unit) => {
    const available = [];
    let current = new Date();
    while (available.length < 10) {
      const dateStr = current.toISOString().split('T')[0];
      const { fullyBooked } = getSlotStatus(dateStr, unit);
      if (!fullyBooked && !isDateBlocked(dateStr, unit) && current.getDay() !== 0) available.push(dateStr);
      current.setDate(current.getDate() + 1);
    }
    return available;
  };

  if (authLoading) {
    return (
      <div className="admin-login">
        <div className="login-box">
          <h2>🔐 Admin Access</h2>
          <p>Checking session…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-login">
        <div className="login-box">
          <h2>🔐 Admin Access</h2>
          <input
            type="email"
            placeholder="Admin email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
          />
          <input
            type="password"
            placeholder="Password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
          />
          <button onClick={handleLogin} disabled={signingIn}>{signingIn ? 'Signing in…' : 'Login'}</button>
          {authError && <p className="scan-error">{authError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>⚙️ Admin Dashboard</h2>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </div>

      <div className="tab-navigation">
        <button className={activeTab === 'verify' ? 'tab-active' : 'tab'} onClick={() => setActiveTab('verify')}>
          ⏳ Verify Payments{pendingVerificationCount > 0 && <span className="tab-badge">{pendingVerificationCount}</span>}
        </button>
        <button className={activeTab === 'availability' ? 'tab-active' : 'tab'} onClick={() => setActiveTab('availability')}>📅 Availability</button>
        <button className={activeTab === 'block' ? 'tab-active' : 'tab'} onClick={() => setActiveTab('block')}>🚫 Block Dates</button>
        <button className={activeTab === 'questions' ? 'tab-active' : 'tab'} onClick={() => setActiveTab('questions')}>❓ Questions</button>
        <button className={activeTab === 'bookings' ? 'tab-active' : 'tab'} onClick={() => setActiveTab('bookings')}>
          📋 Bookings{incompleteCount > 0 && <span className="tab-badge">{incompleteCount}</span>}
        </button>
        <button className={activeTab === 'scan' ? 'tab-active' : 'tab'} onClick={() => setActiveTab('scan')}>📸 Add Slip</button>
      </div>

      {/* VERIFY PAYMENTS TAB */}
      {activeTab === 'verify' && (
        <div className="tab-content">
          <h3>⏳ Verify Payments</h3>
          <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
            These bookings are holding a slot while waiting for staff to confirm the advance actually arrived.
            Unverified bookings release their slot automatically {PENDING_VERIFICATION_MINUTES} minutes after submission.
          </p>
          {pendingVerification.length === 0 ? (
            <p style={{ color: '#999' }}>Nothing waiting on verification right now.</p>
          ) : (
            pendingVerification.map(withPrivateData).map((b) => {
              const unit = getUnit(b);
              const msLeft = b.paymentDeadline ? new Date(b.paymentDeadline).getTime() - now : 0;
              const secondsLeft = Math.max(0, Math.floor(msLeft / 1000));
              const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
              const ss = String(secondsLeft % 60).padStart(2, '0');
              const isUrgent = secondsLeft <= 120;
              const isTimedOut = msLeft <= 0;
              const draftValue = advanceDrafts[b.firestoreId] ?? String(b.advance || '');
              const enteredAdvance = parseInt(draftValue) || 0;
              const previewBalance = Math.max(0, Number(b.totalPrice) - enteredAdvance);
              return (
                <div key={b.firestoreId} className={`verify-card ${isUrgent ? 'verify-urgent' : ''}`}>
                  <div className="verify-card-header">
                    <div>
                      <strong>{b.name}</strong> <span className="booking-serial-tag">· {b.serialNo || '—'}</span>
                      <span className={`adm-unit-badge adm-unit-${unit}`} style={{ marginLeft: '8px' }}>{unit === 'gold' ? '🥇 Gold' : '🥈 Silver'}</span>
                    </div>
                    <span className={`verify-countdown ${isTimedOut ? 'verify-countdown-done' : ''}`}>
                      {isTimedOut ? '⏰ Expiring…' : `⏳ ${mm}:${ss}`}
                    </span>
                  </div>
                  <p>📞 {b.phone || '—'} | 👥 {b.guestCount} persons</p>
                  <p>📅 {formatDatePK(b.checkInDate)} | {b.slotType}</p>
                  <p>💰 Claimed Total: Rs. {Number(b.totalPrice).toLocaleString()} | Claimed Advance: Rs. {Number(b.advance || 0).toLocaleString()}</p>

                  <div className="verify-input-row">
                    <label>Advance actually received (Rs.)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={draftValue}
                      onChange={(e) => setAdvanceDrafts(prev => ({ ...prev, [b.firestoreId]: e.target.value.replace(/[^0-9]/g, '') }))}
                    />
                    <span className="verify-balance-preview">Balance will be: Rs. {previewBalance.toLocaleString()}</span>
                  </div>

                  <div className="verify-actions">
                    <button className="btn-verify-confirm" onClick={() => handleVerifyPayment(b)}>✅ Confirm Booking</button>
                    <button className="btn-verify-expire" onClick={() => handleExpireNow(b)}>❌ Expire Now</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* AVAILABILITY TAB */}
      {activeTab === 'availability' && (
        <div className="tab-content">
          <h3>📅 Check Availability</h3>
          <div className="toggle-group" style={{ marginBottom: '12px' }}>
            <button type="button" className={`toggle-btn ${searchUnit === 'gold' ? 'tgl-gold active' : ''}`} onClick={() => setSearchUnit('gold')}>🥇 GOLD</button>
            <button type="button" className={`toggle-btn ${searchUnit === 'silver' ? 'tgl-silver active' : ''}`} onClick={() => setSearchUnit('silver')}>🥈 SILVER</button>
          </div>
          <input type="date" value={searchDate} onChange={(e) => setSearchDate(e.target.value)} />
          {searchDate && (() => {
            const { dayBooked, nightBooked, fullyBooked } = getSlotStatus(searchDate, searchUnit);
            const blocked = isDateBlocked(searchDate, searchUnit);
            return (
              <div className="availability-result">
                {blocked ? (
                  <div className="booked"><p>🚫 <strong>{formatDatePK(searchDate)}</strong> is BLOCKED (maintenance/event)</p></div>
                ) : fullyBooked ? (
                  <div className="booked">
                    <p>❌ <strong>{formatDatePK(searchDate)}</strong> is FULLY BOOKED</p>
                    <p style={{fontSize:'14px',marginTop:'8px'}}>❌ 8 AM – 6 PM &nbsp;|&nbsp; ❌ 8 PM – 6 AM</p>
                  </div>
                ) : (dayBooked || nightBooked) ? (
                  <div style={{background:'#fff3cd',border:'2px solid #ffc107',borderRadius:'8px',padding:'15px',textAlign:'center'}}>
                    <p style={{fontWeight:'700',color:'#856404',fontSize:'16px'}}>⚠️ <strong>{formatDatePK(searchDate)}</strong> is PARTIALLY BOOKED</p>
                    <p style={{fontSize:'14px',marginTop:'8px',color:'#333'}}>
                      {dayBooked ? '❌' : '✅'} 8 AM – 6 PM (Day Slot)
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                      {nightBooked ? '❌' : '✅'} 8 PM – 6 AM (Night Slot)
                    </p>
                  </div>
                ) : (
                  <div className="available">
                    <p>✅ <strong>{formatDatePK(searchDate)}</strong> is FULLY AVAILABLE</p>
                    <p style={{fontSize:'14px',marginTop:'8px'}}>✅ 8 AM – 6 PM &nbsp;|&nbsp; ✅ 8 PM – 6 AM</p>
                  </div>
                )}
              </div>
            );
          })()}
          <h3 style={{ marginTop: '30px' }}>Next 10 Available Dates</h3>
          <div className="available-dates">
            {getNextAvailableDates(searchUnit).map((date) => (
              <div key={date} className="date-pill">
                {formatDatePK(date)}
              </div>
            ))}
          </div>

          <h3 style={{ marginTop: '30px' }}>🔒 Place a Hold</h3>
          <p style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>
            Temporarily hold a slot (e.g. while confirming details on a call) — blocks it on the public site just like a real booking.
          </p>
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={holdDate} onChange={(e) => setHoldDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Slot</label>
            <div className="toggle-group">
              {[
                { val: '10hour-day', label: '☀️ Day' }, { val: '10hour-night', label: '🌙 Night' }, { val: '22hour', label: '⏱️ 22hr (Whole Day)' },
              ].map(o => (
                <button key={o.val} type="button" className={`toggle-btn ${holdSlotType === o.val ? 'active' : ''}`} onClick={() => setHoldSlotType(o.val)}>{o.label}</button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Held By *</label>
            <div className="toggle-group">
              {HOLD_STAFF.map(name => (
                <button key={name} type="button" className={`toggle-btn ${holdStaff === name ? 'active' : ''}`} onClick={() => setHoldStaff(name)}>{name}</button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Duration</label>
            <div className="toggle-group">
              {HOLD_DURATIONS.map(d => (
                <button key={d.val} type="button" className={`toggle-btn ${holdDuration === d.val ? 'active' : ''}`} onClick={() => setHoldDuration(d.val)}>{d.label}</button>
              ))}
            </div>
          </div>
          <button className="btn-block" onClick={() => handlePlaceHold(searchUnit)}>🔒 Place Hold on {searchUnit === 'gold' ? 'Gold' : 'Silver'}</button>

          <h4 style={{ marginTop: '25px' }}>Active Holds {activeHolds.length > 0 && `(${activeHolds.length})`}</h4>
          <div className="blocked-list">
            {activeHolds.length === 0 ? (
              <p style={{ color: '#999' }}>No active holds</p>
            ) : (
              activeHolds.map(h => (
                <div key={h.firestoreId} className="blocked-item hold-item">
                  <p>
                    <strong>{formatDatePK(h.date)}</strong> — {h.unit === 'gold' ? '🥇 Gold' : '🥈 Silver'} · {h.slotType === '22hour' ? 'Whole Day' : h.slotType === '10hour-night' ? 'Night' : 'Day'}
                    {h.duration === 'indefinite' && <span className="hold-indefinite-badge"> ⚠️ INDEFINITE</span>}
                  </p>
                  <p style={{ fontSize: '12px', color: '#999' }}>Held by <strong>{h.heldBy}</strong> — {formatHoldAge(h.createdAt)}</p>
                  <button className="btn-release-hold" onClick={() => handleReleaseHold(h)}>Release</button>
                </div>
              ))
            )}
          </div>

          <h3 style={{ marginTop: '30px' }}>⚡ Quick Book (Skip Customer Info For Now)</h3>
          <p style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>
            For clearing the physical slip backlog fast — one click marks a slot as booked with no name, phone, or price required yet. Fill those in later from the Bookings tab → "⚡ Needs Info". Unlike a hold, this is a real booking and does <strong>not</strong> auto-expire.
          </p>
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={quickBookDate} onChange={(e) => setQuickBookDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Slot</label>
            <div className="toggle-group">
              {[
                { val: '10hour-day', label: '☀️ Day' }, { val: '10hour-night', label: '🌙 Night' }, { val: '22hour', label: '⏱️ 22hr (Whole Day)' },
              ].map(o => (
                <button key={o.val} type="button" className={`toggle-btn ${quickBookSlotType === o.val ? 'active' : ''}`} onClick={() => setQuickBookSlotType(o.val)}>{o.label}</button>
              ))}
            </div>
          </div>
          <button className="btn-quick-book" onClick={() => handleQuickBook(searchUnit)} disabled={quickBooking}>
            {quickBooking ? '⏳ Booking…' : `⚡ Mark ${searchUnit === 'gold' ? 'Gold' : 'Silver'} Slot as Booked Now`}
          </button>
        </div>
      )}

      {/* BLOCK DATES TAB */}
      {activeTab === 'block' && (
        <div className="tab-content">
          <h3>🚫 Block Dates</h3>
          <div className="form-group">
            <label>Select Date to Block</label>
            <input type="date" value={searchDate} onChange={(e) => setSearchDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Reason</label>
            <select value={blockReason} onChange={(e) => setBlockReason(e.target.value)}>
              <option value="">-- Select Reason --</option>
              <option value="Maintenance">🔧 Maintenance</option>
              <option value="Deep Cleaning">🧹 Deep Cleaning</option>
              <option value="Staff Event">👥 Staff Event</option>
              <option value="Private Event">🎉 Private Event</option>
              <option value="Other">📝 Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Applies To</label>
            <div className="toggle-group">
              <button type="button" className={`toggle-btn ${blockUnit === 'both' ? 'active' : ''}`} onClick={() => setBlockUnit('both')}>🗂️ Both Units</button>
              <button type="button" className={`toggle-btn ${blockUnit === 'gold' ? 'tgl-gold active' : ''}`} onClick={() => setBlockUnit('gold')}>🥇 Gold Only</button>
              <button type="button" className={`toggle-btn ${blockUnit === 'silver' ? 'tgl-silver active' : ''}`} onClick={() => setBlockUnit('silver')}>🥈 Silver Only</button>
            </div>
          </div>
          <button onClick={handleBlockDate} className="btn-block">Block This Date</button>
          <h4 style={{ marginTop: '25px' }}>Blocked Dates</h4>
          <div className="blocked-list">
            {blockedDates.length === 0 ? (
              <p style={{ color: '#999' }}>No blocked dates</p>
            ) : (
              blockedDates.map((b) => (
                <div key={b.firestoreId} className="blocked-item">
                  <p><strong>{formatDatePK(b.date)}</strong> — {b.reason} {b.unit && b.unit !== 'both' && `(${b.unit === 'gold' ? 'Gold' : 'Silver'} only)`}</p>
                  <p style={{ fontSize: '12px', color: '#999' }}>{b.blockedAt ? formatDateTimePK(b.blockedAt) : ''}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* QUESTIONS TAB */}
      {activeTab === 'questions' && (
        <div className="tab-content">
          <h3>❓ Staff Questions</h3>
          <div className="form-group">
            <label>Ask a Question</label>
            <textarea value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} placeholder="e.g. Is June 25 available for 20 people?" rows="3" />
          </div>
          <button onClick={handleAddQuestion} className="btn-ask">Ask Question</button>
          <h4 style={{ marginTop: '25px' }}>Recent Questions</h4>
          <div className="questions-list">
            {questions.length === 0 ? (
              <p style={{ color: '#999' }}>No questions yet</p>
            ) : (
              questions.map((q) => (
                <div key={q.firestoreId} className="question-item">
                  <p className="question-text">❓ {q.question}</p>
                  <p style={{ fontSize: '12px', color: '#999' }}>Asked: {formatDateTimePK(q.askedAt)}</p>
                  {q.answer ? (
                    <p className="question-text" style={{ color: '#2e7d32' }}>✅ {q.answer}</p>
                  ) : (
                    <div className="quick-answer">
                      <input
                        type="text"
                        placeholder="Quick answer..."
                        value={answerDrafts[q.firestoreId] || ''}
                        onChange={(e) => setAnswerDrafts(prev => ({ ...prev, [q.firestoreId]: e.target.value }))}
                        onKeyPress={(e) => e.key === 'Enter' && handleAnswerQuestion(q)}
                      />
                      <button className="btn-answer" onClick={() => handleAnswerQuestion(q)}>Answer</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ADD SLIP TAB */}
      {activeTab === 'scan' && (
        <div className="tab-content">
          <h3>📸 Add Physical Slip</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Fill in the slip details below. Attaching a photo is optional — only useful if you want the auto-fill scan or to keep a copy of the original slip.
          </p>

          {/* Photo preview */}
          {imagePreview && (
            <div className="scan-image-preview">
              <img src={imagePreview} alt="Slip" />
              <button
                onClick={() => { setImagePreview(null); setScanFormData(EMPTY_SCAN); setNicError(''); setLastSavedBooking(null); }}
                style={{ position: 'absolute', top: '10px', right: '10px', background: '#ff6b6b', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}
              >✕</button>
            </div>
          )}

          {/* Upload button */}
          <label className="scan-upload-label">
            📷 {imagePreview ? 'Change Photo' : 'Take or Upload Photo'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onloadend = () => {
                  setImagePreview(reader.result);
                  setScanFormData(EMPTY_SCAN);
                  setNicError('');
                  setLastSavedBooking(null);
                };
                reader.readAsDataURL(file);
              }}
            />
          </label>

          {/* Scan button */}
          {imagePreview && !isScanning && (
            <button
              onClick={() => runScan(imagePreview)}
              style={{ width: '100%', padding: '12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', marginBottom: '15px' }}
            >
              🔍 Scan — Auto-Fill Numbers & Date
            </button>
          )}

          {isScanning && (
            <div className="ocr-processing">
              <div className="ocr-spinner"></div>
              <p>Reading slip... please wait</p>
            </div>
          )}

          {/* Manual form — always visible; the photo above is purely optional
              reference/OCR material, never a gate on entering details. */}
          {!isScanning && (
            <div className="scan-form-section">
              <h4>Fill in the slip details</h4>

              <label className="scan-field-label">S.No (Slip Number)</label>
              <div className="scan-serial-row">
                <input type="text" placeholder="e.g. 0238 (paper slip) or leave blank" value={scanFormData.serialNo} onChange={(e) => setScanFormData({ ...scanFormData, serialNo: e.target.value })} />
                <button
                  type="button"
                  className="btn-autogen-serial"
                  disabled={generatingSerial}
                  onClick={async () => {
                    setGeneratingSerial(true);
                    try {
                      const sn = await getNextWebSerial();
                      setScanFormData(prev => ({ ...prev, serialNo: sn }));
                    } catch (err) {
                      alert('❌ Failed to generate serial: ' + err.message);
                    } finally {
                      setGeneratingSerial(false);
                    }
                  }}
                >
                  {generatingSerial ? '…' : '🔢 Auto (W#)'}
                </button>
              </div>
              <p className="scan-generate-hint scan-generate-hint--left">Auto (W#) pulls the next number from the same sequence used for online bookings — use it for slips that should count as a web booking.</p>

              <label className="scan-field-label">Guest Name</label>
              <input type="text" placeholder="Full name" value={scanFormData.name} onChange={(e) => setScanFormData({ ...scanFormData, name: e.target.value })} />

              <label className="scan-field-label">Contact No</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="03XXXXXXXXX" value={scanFormData.phone} onChange={(e) => setScanFormData({ ...scanFormData, phone: e.target.value.replace(/\D/g, '') })} maxLength="11" />

              <label className="scan-field-label">NIC No (13 digits, no dashes)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="42101xxxxxxx3"
                value={scanFormData.nicNo}
                maxLength="13"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 13);
                  setScanFormData({ ...scanFormData, nicNo: val });
                  setNicError(val.length > 0 && val.length !== 13 ? 'NIC must be exactly 13 digits' : '');
                }}
              />
              {nicError && <p className="scan-error">{nicError}</p>}

              <label className="scan-field-label">No of Persons</label>
              <div className="toggle-group">
                {(CONFIGS[(scanFormData.unit || 'Gold').toLowerCase()]?.personSteps || []).map(n => (
                  <button key={n} type="button"
                    className={`toggle-btn ${String(scanFormData.guestCount) === String(n) ? 'active' : ''}`}
                    onClick={() => setScanFormData({ ...scanFormData, guestCount: String(n) })}>
                    {n}
                  </button>
                ))}
              </div>
              <input type="number" min="1" max="500" placeholder="Or type an exact number" value={scanFormData.guestCount} onChange={(e) => setScanFormData({ ...scanFormData, guestCount: e.target.value })} />

              <label className="scan-field-label">Slot Type</label>
              <div className="toggle-group">
                {[
                  { val: '10hour-day',   label: '☀️ Day 8AM–6PM',   cls: 'tgl-day' },
                  { val: '10hour-night', label: '🌙 Night 8PM–6AM', cls: 'tgl-night' },
                  { val: '22hour',       label: '⏱️ 22 Hours',      cls: 'tgl-22hr' },
                ].map(o => (
                  <button key={o.val} type="button"
                    className={`toggle-btn ${scanFormData.slotType === o.val ? o.cls + ' active' : ''}`}
                    onClick={() => setScanFormData({ ...scanFormData, slotType: o.val })}>
                    {o.label}
                  </button>
                ))}
              </div>

              <label className="scan-field-label">Booking Type</label>
              <div className="toggle-group">
                {[
                  { val: 'family',  label: '👨‍👩‍👧‍👦 Family',  cls: 'tgl-family' },
                  { val: 'friends', label: '👥 Friends', cls: 'tgl-friends' },
                ].map(o => (
                  <button key={o.val} type="button"
                    className={`toggle-btn ${scanFormData.bookingType === o.val ? o.cls + ' active' : ''}`}
                    onClick={() => setScanFormData({ ...scanFormData, bookingType: o.val })}>
                    {o.label}
                  </button>
                ))}
              </div>

              <label className="scan-field-label">Date</label>
              <input
                type="date"
                value={scanFormData.checkInDate}
                onChange={(e) => {
                  const d = e.target.value;
                  setScanFormData({ ...scanFormData, checkInDate: d, day: getDayName(d) });
                }}
              />

              <label className="scan-field-label">Day</label>
              <input type="text" placeholder="Auto-filled from date" value={scanFormData.day} onChange={(e) => setScanFormData({ ...scanFormData, day: e.target.value })} />

              <label className="scan-field-label">Unit</label>
              <div className="toggle-group">
                {[
                  { val: 'Gold',   label: '🥇 GOLD',   cls: 'tgl-gold' },
                  { val: 'Silver', label: '🥈 SILVER', cls: 'tgl-silver' },
                ].map(o => (
                  <button key={o.val} type="button"
                    className={`toggle-btn ${scanFormData.unit === o.val ? o.cls + ' active' : ''}`}
                    onClick={() => setScanFormData({ ...scanFormData, unit: o.val })}>
                    {o.label}
                  </button>
                ))}
              </div>

              <label className="scan-field-label">AC (Gold has 4 AC rooms)</label>
              <div className="toggle-group">
                {[
                  { val: '1', label: '1 AC' },
                  { val: '2', label: '2 AC' },
                  { val: '3', label: '3 AC' },
                  { val: '4', label: '4 AC' },
                  { val: 'none', label: 'Non-AC' },
                ].map(o => (
                  <button key={o.val} type="button"
                    className={`toggle-btn ${scanFormData.acCount === o.val ? 'active' : ''}`}
                    onClick={() => setScanFormData({ ...scanFormData, acCount: o.val })}>
                    {o.label}
                  </button>
                ))}
              </div>

              <label className="scan-field-label">Total (Rs.)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 55000"
                value={scanFormData.totalPrice}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  const total = parseInt(raw) || 0;
                  const adv = parseInt(scanFormData.advance) || 0;
                  setScanFormData({ ...scanFormData, totalPrice: raw, balance: total > 0 ? String(Math.max(0, total - adv)) : scanFormData.balance });
                }}
              />
              {(() => {
                const std = calculateStandardPrice(scanFormData.unit, scanFormData.slotType, scanFormData.checkInDate);
                const entered = parseInt(scanFormData.totalPrice) || 0;
                const diff = std > 0 && entered > 0 ? std - entered : 0;
                return std > 0 ? (
                  <div className="price-calc-info">
                    <p>Standard Rate: <strong>Rs. {std.toLocaleString()}</strong></p>
                    {diff > 0 && <p className="discount-badge">Discount Given: Rs. {diff.toLocaleString()}</p>}
                    {diff < 0 && <p className="surcharge-badge">Above Standard: Rs. {Math.abs(diff).toLocaleString()}</p>}
                  </div>
                ) : null;
              })()}

              <label className="scan-field-label">Advance (Rs.)</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 27500"
                value={scanFormData.advance}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  const total = parseInt(scanFormData.totalPrice) || 0;
                  const adv = parseInt(raw) || 0;
                  setScanFormData({ ...scanFormData, advance: raw, balance: total > 0 ? String(Math.max(0, total - adv)) : scanFormData.balance });
                }}
              />

              <label className="scan-field-label">Balance (Rs.)</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="Auto-calculated" value={scanFormData.balance} onChange={(e) => setScanFormData({ ...scanFormData, balance: e.target.value.replace(/[^0-9]/g, '') })} />

              <label className="scan-field-label">Payment Status</label>
              <div className="toggle-group">
                {[
                  { val: 'Pending',   label: '⏳ Pending',   cls: 'tgl-pending' },
                  { val: 'Completed', label: '✅ Completed', cls: 'tgl-completed' },
                ].map(o => (
                  <button key={o.val} type="button"
                    className={`toggle-btn ${scanFormData.paymentStatus === o.val ? o.cls + ' active' : ''}`}
                    onClick={() => setScanFormData({ ...scanFormData, paymentStatus: o.val })}>
                    {o.label}
                  </button>
                ))}
              </div>

              <div className="scan-form-actions">
                <button
                  className="btn-scan-save"
                  onClick={async () => {
                    const newBooking = buildBookingFromScanForm();
                    if (!newBooking) return;

                    // Add Slip used to write straight to Firestore with no check at
                    // all — a staff member transcribing a paper slip could silently
                    // double-book a date/unit/slot that's already taken or on hold.
                    // Reuses the exact same getSlotStatus check Quick Book already
                    // relies on (which itself folds in both bookings and active holds).
                    if (isDateBlocked(newBooking.checkInDate, newBooking.farmhouse)) {
                      alert(`❌ ${newBooking.unit} — ${newBooking.checkInDate} is blocked (maintenance/event). Remove the block first if this slip is legitimate.`);
                      return;
                    }
                    const { dayBooked, nightBooked } = getSlotStatus(newBooking.checkInDate, newBooking.farmhouse);
                    const conflict =
                      (newBooking.slotType === '10hour-day'   && dayBooked) ||
                      (newBooking.slotType === '10hour-night' && nightBooked) ||
                      (newBooking.slotType === '22hour'       && (dayBooked || nightBooked));
                    if (conflict) {
                      alert(`❌ ${newBooking.unit} already has a booking or active hold on ${newBooking.checkInDate} for this slot — saving this would double-book it. Check the Bookings tab and Quick-Check holds, and fix or cancel the existing entry first.`);
                      return;
                    }

                    try {
                      const result = await saveBooking(newBooking);
                      setImagePreview(null);
                      setLastSavedBooking({ ...newBooking, serialNo: result?.serialNo || newBooking.serialNo, firestoreId: result?.id });
                      setScanFormData(EMPTY_SCAN);
                      setNicError('');
                    } catch (err) {
                      alert('❌ Failed to save to Firebase: ' + err.message);
                    }
                  }}
                >
                  ✅ Save Booking
                </button>
              </div>

              <div className="scan-form-actions scan-form-actions--slip">
                <button
                  className="btn-generate-slip"
                  onClick={() => {
                    const draft = draftBookingOrAlert();
                    if (draft) handleDownloadSlip(draft);
                  }}
                >
                  ⬇️ Save as PDF
                </button>
                <button
                  className="btn-generate-slip"
                  onClick={() => {
                    const draft = draftBookingOrAlert();
                    if (draft) handleDownloadSlipImage(draft);
                  }}
                >
                  🖼️ Save as Image
                </button>
              </div>
              <div className="scan-form-actions scan-form-actions--slip">
                <button
                  className="btn-share-slip-inline"
                  onClick={() => {
                    const draft = draftBookingOrAlert();
                    if (draft) handleShareSlipPdf(draft);
                  }}
                >
                  📤 Share PDF
                </button>
                <button
                  className="btn-share-slip-inline"
                  onClick={() => {
                    const draft = draftBookingOrAlert();
                    if (draft) handleShareSlipImage(draft);
                  }}
                >
                  📤 Share Image
                </button>
              </div>
              <p className="scan-generate-hint">Generates the slip straight from the fields above — no need to save first.</p>

              {lastSavedBooking && (
                <div className="slip-ready-box">
                  <p>✅ Saved as <strong>{lastSavedBooking.serialNo}</strong></p>
                  <div className="slip-ready-box__actions">
                    <button className="btn-generate-slip" onClick={() => handleDownloadSlip(lastSavedBooking)}>⬇️ PDF</button>
                    <button className="btn-generate-slip" onClick={() => handleDownloadSlipImage(lastSavedBooking)}>🖼️ Image</button>
                  </div>
                  <div className="slip-ready-box__actions">
                    <button className="btn-share-slip-inline" onClick={() => handleShareSlipPdf(lastSavedBooking)}>📤 Share PDF</button>
                    <button className="btn-share-slip-inline" onClick={() => handleShareSlipImage(lastSavedBooking)}>📤 Share Image</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* BOOKINGS TAB */}
      {activeTab === 'bookings' && (
        <div className="tab-content">
          <div className="bookings-tab-header">
            <h3>📋 All Bookings</h3>
            <div className="booking-filter-group">
              {[
                { val: 'all',       label: '🗂️ All' },
                { val: 'gold',      label: '🥇 Gold' },
                { val: 'silver',    label: '🥈 Silver' },
                { val: 'incomplete', label: `⚡ Needs Info${incompleteCount > 0 ? ` (${incompleteCount})` : ''}` },
                { val: 'balance',   label: '⏳ Remaining Balance' },
                { val: 'fullypaid', label: '✅ Fully Paid' },
                { val: 'cancelled', label: '🚫 Cancelled' },
              ].map(f => (
                <button
                  key={f.val}
                  className={`bfilt-btn ${bookingFilter === f.val ? 'bfilt-active-' + f.val : ''}`}
                  onClick={() => setBookingFilter(f.val)}
                >{f.label}</button>
              ))}
            </div>
          </div>

          <input
            type="text"
            className="booking-search-input"
            placeholder="🔍 Search by name, phone, NIC, serial number, or date…"
            value={bookingSearch}
            onChange={(e) => setBookingSearch(e.target.value)}
          />

          <div className="sync-availability-box">
            <p>Seeing a real booking show as "available" on the public site or Quick-Check? That means it predates the availability-sync system.</p>
            <button className="btn-sync-availability" onClick={handleSyncAvailability} disabled={syncingAvailability}>
              {syncingAvailability ? '⏳ Syncing…' : '🔧 Sync Availability Records'}
            </button>
          </div>

          {(() => {
            const q = bookingSearch.trim().toLowerCase();
            const matchesSearch = (b) => {
              if (!q) return true;
              const haystack = [b.name, b.phone, b.nicNo, b.serialNo, b.checkInDate, formatDatePK(b.checkInDate)]
                .filter(Boolean).join(' ').toLowerCase();
              return haystack.includes(q);
            };
            const filtered = bookings
              .map(withPrivateData)
              .filter(b => {
                const isPaid = b.paymentStatus === 'Completed' || Number(b.balance) === 0;
                switch (bookingFilter) {
                  case 'all': return true;
                  case 'incomplete': return b.infoComplete === false;
                  case 'cancelled': return b.status === 'cancelled';
                  // Payment-status filters only make sense for live bookings —
                  // a cancelled booking's leftover balance isn't something
                  // anyone's still chasing, so it's excluded from both.
                  case 'balance': return b.status !== 'cancelled' && !isPaid;
                  case 'fullypaid': return b.status !== 'cancelled' && isPaid;
                  default: return getUnit(b) === bookingFilter;
                }
              })
              .filter(matchesSearch)
              .sort((a, b) => (a.checkInDate || '').localeCompare(b.checkInDate || ''));
            const goldCount   = bookings.filter(b => getUnit(b) === 'gold').length;
            const silverCount = bookings.filter(b => getUnit(b) === 'silver').length;

            return (
              <>
                <p className="bookings-count-row">
                  <span className="bc-gold">🥇 Gold: {goldCount}</span>
                  <span className="bc-silver">🥈 Silver: {silverCount}</span>
                  <span className="bc-total">Total: {bookings.length}</span>
                </p>
                {bookingFilter !== 'all' && (
                  <p className="bookings-filter-count">Showing {filtered.length} of {bookings.length} bookings</p>
                )}
                <div className="bookings-admin">
                  {filtered.length === 0 ? (
                    <p style={{ color: '#999' }}>No bookings found</p>
                  ) : (
                    filtered.map((b) => {
                      const unit = getUnit(b);
                      const isPaid = b.paymentStatus === 'Completed' || Number(b.balance) === 0;
                      const slotLabel =
                        b.slotType === '10hour-day'   ? '☀️ 8 AM – 6 PM (Day)'   :
                        b.slotType === '10hour-night'  ? '🌙 8 PM – 6 AM (Night)' :
                        b.slotType === '22hour'        ? '⏱️ 8 AM – 6 AM (22hr)'  :
                        b.slotType === '10hour'        ? '⏱️ 10 Hours (Legacy)'   : b.slotType || '—';
                      const bookedOn = formatDateTimePK(b.createdAt);
                      const statusBadge =
                        b.status === 'pending_verification' ? <span className="status status-pending">⏳ Awaiting Verification</span> :
                        b.status === 'expired'              ? <span className="status status-expired">⏰ Expired</span> :
                        b.status === 'cancelled'             ? <span className="status status-cancelled">🚫 Cancelled</span> :
                        <span className="status confirmed">✅ Confirmed</span>;
                      const isEditing = editingId === b.firestoreId;
                      const needsInfo = b.infoComplete === false;
                      return (
                        <div key={b.id || b.firestoreId} className={`booking-admin-card adm-card-${unit}`}>
                          <div className="booking-admin-header">
                            <strong>{b.name || '⚡ (Name Not Entered Yet)'} <span className="booking-serial-tag">· {b.serialNo || '—'}</span></strong>
                            <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                              <span className={`adm-unit-badge adm-unit-${unit}`}>{unit === 'gold' ? '🥇 Gold' : '🥈 Silver'}</span>
                              {needsInfo && <span className="status status-needs-info">⚡ Needs Info</span>}
                              {statusBadge}
                            </div>
                          </div>

                          {isEditing ? (
                            <div className="edit-booking-form">
                              <label className="scan-field-label">Name</label>
                              <input type="text" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} />

                              <label className="scan-field-label">Date</label>
                              <input type="date" value={editDraft.checkInDate} onChange={(e) => setEditDraft({ ...editDraft, checkInDate: e.target.value })} />

                              <label className="scan-field-label">Unit</label>
                              <div className="toggle-group">
                                {['Gold', 'Silver'].map(u => (
                                  <button key={u} type="button" className={`toggle-btn ${editDraft.unit === u ? (u === 'Gold' ? 'tgl-gold' : 'tgl-silver') + ' active' : ''}`}
                                    onClick={() => setEditDraft({ ...editDraft, unit: u })}>{u === 'Gold' ? '🥇 Gold' : '🥈 Silver'}</button>
                                ))}
                              </div>

                              <label className="scan-field-label">Slot Type</label>
                              <div className="toggle-group">
                                {[
                                  { val: '10hour-day', label: '☀️ Day' }, { val: '10hour-night', label: '🌙 Night' }, { val: '22hour', label: '⏱️ 22hr' },
                                ].map(o => (
                                  <button key={o.val} type="button" className={`toggle-btn ${editDraft.slotType === o.val ? 'active' : ''}`}
                                    onClick={() => setEditDraft({ ...editDraft, slotType: o.val })}>{o.label}</button>
                                ))}
                              </div>

                              <label className="scan-field-label">Guests</label>
                              <input type="number" min="1" value={editDraft.guestCount} onChange={(e) => setEditDraft({ ...editDraft, guestCount: e.target.value })} />

                              <label className="scan-field-label">Total (Rs.)</label>
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={editDraft.totalPrice} onChange={(e) => setEditDraft({ ...editDraft, totalPrice: e.target.value.replace(/\D/g, '') })} />

                              <label className="scan-field-label">Advance (Rs.)</label>
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={editDraft.advance} onChange={(e) => setEditDraft({ ...editDraft, advance: e.target.value.replace(/\D/g, '') })} />

                              <p className="verify-balance-preview">
                                Balance will be: Rs. {Math.max(0, (parseInt(editDraft.totalPrice) || 0) - (parseInt(editDraft.advance) || 0)).toLocaleString()}
                              </p>

                              <div className="private-info-section">
                                <p className="private-info-hint">🔒 Contact No / NIC / Slip Serial can only be entered once — they lock permanently after saving.</p>
                                {b.phone ? <p>📞 {b.phone} <span className="locked-tag">🔒 locked</span></p> : (
                                  <>
                                    <label className="scan-field-label">Contact No (optional)</label>
                                    <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="03XXXXXXXXX" maxLength="11" value={editDraft.phone}
                                      onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value.replace(/\D/g, '') })} />
                                  </>
                                )}
                                {b.nicNo ? <p>🪪 {b.nicNo} <span className="locked-tag">🔒 locked</span></p> : (
                                  <>
                                    <label className="scan-field-label">NIC No (optional, 13 digits)</label>
                                    <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="42101xxxxxxx3" maxLength="13" value={editDraft.nicNo}
                                      onChange={(e) => setEditDraft({ ...editDraft, nicNo: e.target.value.replace(/\D/g, '').slice(0, 13) })} />
                                  </>
                                )}
                                {b.serialNo ? <p>🔖 {b.serialNo} <span className="locked-tag">🔒 locked</span></p> : (
                                  <>
                                    <label className="scan-field-label">Slip Serial No (optional)</label>
                                    <input type="text" placeholder="e.g. 0259" value={editDraft.serialNo}
                                      onChange={(e) => setEditDraft({ ...editDraft, serialNo: e.target.value })} />
                                  </>
                                )}
                              </div>

                              <div className="verify-actions">
                                <button className="btn-verify-confirm" onClick={() => handleSaveEdit(b)}>💾 Save Changes</button>
                                <button className="btn-verify-expire" onClick={handleCancelEdit}>✕ Cancel Edit</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p>📞 {b.phone || '—'}{b.nicNo && ` | 🪪 ${b.nicNo}`}</p>
                              <p>👥 {b.guestCount} persons | {b.bookingType === 'family' ? '👨‍👩‍👧‍👦 Family' : '👥 Friends'}</p>
                              <p>📅 {formatDatePK(b.checkInDate)} | {slotLabel}</p>
                              <p>💰 Rs. {Number(b.totalPrice).toLocaleString()} | Adv: Rs. {Number(b.advance || 0).toLocaleString()} | Bal: Rs. {Number(b.balance || 0).toLocaleString()}</p>
                              {b.status === 'confirmed' && (
                                <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',marginTop:'6px'}}>
                                  {isPaid
                                    ? <span className="adm-pay-badge adm-pay-done">✅ Fully Paid</span>
                                    : <span className="adm-pay-badge adm-pay-pending">⏳ Balance Rs. {Number(b.balance || 0).toLocaleString()} Due</span>
                                  }
                                  {!isPaid && b.firestoreId && (
                                    <button
                                      className="btn-mark-paid"
                                      onClick={async () => {
                                        if (!window.confirm(`Mark ${b.name}'s booking as FULLY PAID?`)) return;
                                        try {
                                          await updateBooking(b.firestoreId, { paymentStatus: 'Completed', balance: 0 });
                                        } catch (err) {
                                          alert('Failed to update: ' + err.message);
                                        }
                                      }}
                                    >✅ Mark Fully Paid</button>
                                  )}
                                  <button className="btn-share-slip" onClick={() => handleDownloadSlip(b)}>⬇️ PDF</button>
                                  <button className="btn-share-slip" onClick={() => handleDownloadSlipImage(b)}>🖼️ Image</button>
                                  <button className="btn-share-slip" onClick={() => handleShareSlipPdf(b)}>📤 Share PDF</button>
                                  <button className="btn-share-slip" onClick={() => handleShareSlipImage(b)}>📤 Share Image</button>
                                </div>
                              )}
                              {isPaid && b.status === 'confirmed' && (
                                <div className="paid-slip-group">
                                  <span className="paid-slip-group__label">✅ FULLY PAID SLIP</span>
                                  <button className="btn-paid-slip" onClick={() => handleDownloadPaidSlip(b)}>⬇️ PDF</button>
                                  <button className="btn-paid-slip" onClick={() => handleDownloadPaidSlipImage(b)}>🖼️ Image</button>
                                  <button className="btn-paid-slip" onClick={() => handleSharePaidSlipPdf(b)}>📤 Share PDF</button>
                                  <button className="btn-paid-slip" onClick={() => handleSharePaidSlipImage(b)}>📤 Share Image</button>
                                </div>
                              )}
                              {(b.status === 'confirmed' || b.status === 'cancelled') && (
                                <div className="cancel-slip-group">
                                  <span className="cancel-slip-group__label">
                                    {b.status === 'cancelled' ? '📄 CANCELLED SLIP' : '❌ CANCEL THIS SLIP'}
                                  </span>
                                  <button className="btn-cancel-slip" onClick={() => handleDownloadCancelledSlip(b)}>⬇️ PDF</button>
                                  <button className="btn-cancel-slip" onClick={() => handleDownloadCancelledSlipImage(b)}>🖼️ Image</button>
                                  <button className="btn-cancel-slip" onClick={() => handleShareCancelledSlipPdf(b)}>📤 Share PDF</button>
                                  <button className="btn-cancel-slip" onClick={() => handleShareCancelledSlipImage(b)}>📤 Share Image</button>
                                </div>
                              )}
                              {b.status === 'pending_verification' && (
                                <p className="verify-hint">→ Head to the <strong>Verify Payments</strong> tab to confirm this one.</p>
                              )}
                              <p style={{ fontSize: '12px', color: '#888', marginTop:'6px' }}>Booked: {bookedOn}</p>

                              <div className="admin-record-actions">
                                <button className="btn-edit-booking" onClick={() => handleStartEdit(b)}>✏️ Edit</button>
                                {b.status !== 'cancelled' && (
                                  <button className="btn-cancel-booking" onClick={() => handleCancelBooking(b)}>🚫 Cancel</button>
                                )}
                                <button className="btn-hard-delete" onClick={() => handleHardDelete(b)}>🗑️ Permanently Delete (cannot be undone)</button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
